import { useCallback, useEffect } from 'react';

import { LinkTab, TabContent, Tabs } from 'ms-components/Tabs';
import { useSnowplow } from 'ms-helpers/Snowplow/useSnowplow';
import {
  validateLegacyTextbookSubtopicActiveTab,
  type LegacyTextbookSubtopicActiveTab,
} from 'ms-pages/Textbooks/components/SubtopicDetailView/helpers';
import { useAccessibilityMode } from 'ms-utils/accessibility';
import useQueryParam from 'ms-utils/hooks/useQueryParam';

type Props = {
  subtopicId: string;
  hasWorksheet: boolean;
  hasExercise: boolean;
  hasTeacherNote: boolean;
  onSelectTab?: ((tab: LegacyTextbookSubtopicActiveTab) => void) | undefined;
};

export function FreeTextbookSubtopicTabs({
  hasWorksheet,
  hasExercise,
  hasTeacherNote,
  onSelectTab,
  subtopicId,
}: Props) {
  const [hasEnabledAccessibilityMode] = useAccessibilityMode();

  const [activeTab, _setActiveTab, getTabLink] =
    useQueryParam<LegacyTextbookSubtopicActiveTab>(
      'activeTab',
      'theory',
      validateLegacyTextbookSubtopicActiveTab,
    );

  const setActiveTab = useCallback(
    (newTab: LegacyTextbookSubtopicActiveTab) => {
      _setActiveTab(newTab);
      if (onSelectTab != null) onSelectTab(newTab);
    },
    [_setActiveTab, onSelectTab],
  );

  useEffect(() => {
    if (hasEnabledAccessibilityMode) {
      setActiveTab('worksheet');
      return;
    }
    // If assignableTasks are not part of the current user's subscription
    // plan, we must not set that tab as the default.
    if (!hasExercise && activeTab === 'interactive') {
      setActiveTab('theory');
    }

    if (!hasWorksheet && activeTab === 'worksheet') {
      setActiveTab('theory');
    }

    if (!hasTeacherNote && activeTab === 'teacherNotes') {
      setActiveTab('theory');
    }
  }, [
    activeTab,
    hasEnabledAccessibilityMode,
    hasExercise,
    hasTeacherNote,
    hasWorksheet,
    setActiveTab,
  ]);

  const { trackStructEvent } = useSnowplow();

  // In accessibility mode worksheets are the only content available
  // We want to render an empty view in the subtopic detail view without the tabs
  if (
    hasEnabledAccessibilityMode &&
    hasWorksheet &&
    activeTab === 'worksheet'
  ) {
    return null;
  }

  return (
    <Tabs>
      {hasTeacherNote && (
        <LinkTab
          active={activeTab === 'teacherNotes'}
          to={getTabLink('teacherNotes')}
        >
          <TabContent>Teacher Notes</TabContent>
        </LinkTab>
      )}
      {!hasEnabledAccessibilityMode && (
        <LinkTab
          active={activeTab === 'theory'}
          to={getTabLink('theory')}
          onClick={() => {
            trackStructEvent({
              category: 'free_textbook',
              action: 'selected_theory_tab',
              property: 'subtopicId',
              label: subtopicId,
            });
          }}
        >
          <TabContent>Lesson</TabContent>
        </LinkTab>
      )}

      {hasWorksheet && (
        <LinkTab
          active={activeTab === 'worksheet'}
          to={getTabLink('worksheet')}
          onClick={() => {
            trackStructEvent({
              category: 'free_textbook',
              action: 'selected_worksheet_tab',
              property: 'subtopicId',
              label: subtopicId,
            });
          }}
        >
          <TabContent>Worksheet</TabContent>
        </LinkTab>
      )}

      {hasExercise && !hasEnabledAccessibilityMode && (
        <LinkTab
          active={activeTab === 'interactive'}
          to={getTabLink('interactive')}
          onClick={() => {
            trackStructEvent({
              category: 'free_textbook',
              action: 'selected_practice_tab',
              property: 'subtopicId',
              label: subtopicId,
            });
          }}
        >
          <TabContent>Practice</TabContent>
        </LinkTab>
      )}
    </Tabs>
  );
}
